import { useCallback, useState } from 'react';

import { usePlayingState } from '@/scenarios/legacy-player/hooks/usePlayingState';
import { useInstanceCallback } from '@/scenarios/legacy-player/hooks/useInstanceCallback';
import { AudioTrack, TextTrack } from '@/scenarios/legacy-player/types/instance';
import { useInstanceEffect } from '@/scenarios/legacy-player/hooks/useInstanceEffect';
import { useOnCurrentAudioTrackChanged } from '@/scenarios/legacy-player/hooks/useOnCurrentAudioTrackChanged';

import { isPrimaryLangTrack } from '../../../../../Player/lib/utils/trackLanguage';

export const useIsSubtitlesToggleEnabled = (forceSubtitles?: boolean): boolean => {
    const [isSubtitlesToggleEnabled, setSubtitlesToggleEnabled] = useState(true);
    const { playingState } = usePlayingState();

    const enableSubtitles = useInstanceCallback((instance, textTracks: readonly TextTrack[]) => {
        if (!instance) {
            return;
        }

        const rusTextTrack = textTracks.find(isPrimaryLangTrack);

        if (rusTextTrack) {
            instance.setTextTrack(rusTextTrack);
        }
    });

    const onCurrentAudioLangChanged = useCallback(
        ({
            audioTrack,
            textTrack,
            textTracks,
        }: {
            audioTrack: AudioTrack;
            textTracks: readonly TextTrack[];
            textTrack?: TextTrack;
        }) => {
            const isCurrentTrackRussian = isPrimaryLangTrack(audioTrack);

            setSubtitlesToggleEnabled(isCurrentTrackRussian);

            if (!isCurrentTrackRussian && !textTrack) {
                enableSubtitles(textTracks);
            }
        },
        [enableSubtitles]
    );

    const onCurrentAudioTrackChanged = useInstanceCallback(
        (instance, audioTrack: AudioTrack | undefined) => {
            if (audioTrack && instance) {
                const { textTracks, textTrack } = instance;

                onCurrentAudioLangChanged({
                    audioTrack,
                    textTracks: textTracks || [],
                    textTrack,
                });
            }
        },
        [onCurrentAudioLangChanged]
    );

    useInstanceEffect(
        ({ audioTrack, textTracks, textTrack }) => {
            if (forceSubtitles && audioTrack) {
                onCurrentAudioLangChanged({ audioTrack, textTracks: textTracks || [], textTrack });
            }
        },
        [forceSubtitles, onCurrentAudioLangChanged, playingState]
    );

    useOnCurrentAudioTrackChanged(forceSubtitles ? onCurrentAudioTrackChanged : undefined);

    return isSubtitlesToggleEnabled;
};
